/**
 * Adaptive resolution: the game canvas renders at up to 2 device pixels per CSS pixel and
 * steps the resolution down when frames are slow (and back up when there is headroom), so
 * slow phones keep a steady frame rate instead of stuttering at full sharpness.
 *
 * Judged per window of WINDOW_MS:
 *  - late frames: intervals clearly longer than the display's usual one, but never shorter
 *    than a 60 Hz frame (90 and 120 Hz displays get a steady 60 fps, not a blurry 120; 30 fps
 *    power-saving modes work too);
 *  - how busy each frame keeps the main thread (script, style, layout, canvas drawing),
 *    which tells a heavy frame from a slow display.
 * After a step down, stepping up stops just below the level that was too slow for a while
 * (no see-saw between two levels).
 */

export const MAX_PIXEL_RATIO = 2;
/** Lowest resolution multiplier (of the device's capped pixel ratio). */
const MIN_QUALITY = 0.5;
/** Never below this many device pixels per CSS pixel. */
const MIN_PIXEL_RATIO = 0.75;
const STEP_DOWN = 0.82;
const STEP_UP = 1 / 0.9;
const WINDOW_MS = 1500;
const WARMUP_MS = 2000;
const COOLDOWN_MS = 2500;
/** Windows in a row with headroom before stepping up again. */
const CALM_WINDOWS = 4;
/** Busier frames than this (mean) leave too little of a 60 Hz frame for the slow ones. */
const BUSY_MS = 11;
/** Frames this light leave room for more pixels. */
const LIGHT_MS = 7;
/** How long the level that was too slow stays off-limits. */
const CEILING_MS = 30_000;
/**
 * The shortest frame interval the resolution is kept up for (a hair under 60 Hz): on a 90 or
 * 120 Hz screen, frames that take two refreshes still make a smooth 60 fps, and chasing the
 * faster rate would only cost sharpness.
 */
const TARGET_INTERVAL_MS = 1000 / 61;

/** Device pixels per CSS pixel for the canvas: the device's, capped, times the quality. */
export function pixelRatio(devicePixelRatio: number, quality: number): number {
  const device = Math.min(MAX_PIXEL_RATIO, devicePixelRatio > 0 ? devicePixelRatio : 1);
  return Math.max(Math.min(device, MIN_PIXEL_RATIO), device * quality);
}

export class AdaptiveQuality {
  /** Multiplier of the capped device pixel ratio, MIN_QUALITY..1. */
  quality = 1;
  private windowStart = 0;
  /** Frame intervals of the current window. */
  private readonly window: number[] = [];
  /** Sum of the busy times of the current window. */
  private work = 0;
  private calmWindows = 0;
  private quietUntil = 0;
  /**
   * The display's frame interval: the shortest usual interval seen so far. A display that
   * ran at 60 Hz and now gets a frame every 33 ms has late frames.
   */
  private display = Number.POSITIVE_INFINITY;
  /** Upper limit for stepping up (just below a level that was too slow) and until when. */
  private ceiling = 1;
  private ceilingUntil = 0;

  /** Starts over (new view size, page visible again): the next frames are not judged. */
  reset(now: number): void {
    this.windowStart = now;
    this.window.length = 0;
    this.work = 0;
    this.calmWindows = 0;
    this.quietUntil = now + WARMUP_MS;
  }

  /**
   * Records one frame: `interval` since the previous frame and how long the frame kept the
   * main thread `busy`, both ms. Returns the new quality when it changed, otherwise null.
   */
  record(now: number, interval: number, busy: number): number | null {
    if (now < this.quietUntil || interval <= 0 || interval > 250) return null;
    this.window.push(interval);
    this.work += busy;
    if (now - this.windowStart < WINDOW_MS) return null;
    const typical = lowQuartile(this.window);
    this.display = Math.max(TARGET_INTERVAL_MS, Math.min(this.display, typical));
    let late = 0;
    for (const value of this.window) if (value > this.display * 1.5) late++;
    const lateShare = late / this.window.length;
    const meanBusy = this.work / this.window.length;
    this.windowStart = now;
    this.window.length = 0;
    this.work = 0;
    if (now > this.ceilingUntil) this.ceiling = 1;

    const struggling = lateShare > 0.2 || meanBusy > BUSY_MS;
    if (struggling && this.quality > MIN_QUALITY) {
      this.ceiling = Math.max(MIN_QUALITY, this.quality * 0.95);
      this.ceilingUntil = now + CEILING_MS;
      this.quality = Math.max(MIN_QUALITY, this.quality * STEP_DOWN);
      this.calmWindows = 0;
      this.quietUntil = now + COOLDOWN_MS;
      return this.quality;
    }
    const calm = lateShare < 0.04 && meanBusy < LIGHT_MS;
    if (!calm) {
      this.calmWindows = 0;
      return null;
    }
    const next = Math.min(1, this.ceiling, this.quality * STEP_UP);
    if (++this.calmWindows >= CALM_WINDOWS && next > this.quality + 0.01) {
      this.quality = next;
      this.calmWindows = 0;
      this.quietUntil = now + COOLDOWN_MS;
      return this.quality;
    }
    return null;
  }
}

/** The 25th percentile: the usual frame interval, robust against hiccups. */
function lowQuartile(values: readonly number[]): number {
  if (values.length === 0) return 1000 / 60;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length * 0.25)]!;
}
